import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSessionContext } from '../context/SessionContext';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { FiPlus, FiSearch, FiClock, FiCalendar, FiUser, FiActivity, FiEdit, FiTrash2 } from 'react-icons/fi';
import { formatDate, formatDuration } from '../lib/utils';

const Sessions = () => {
  const { sessions, deleteSession } = useSessionContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const navigate = useNavigate();

  const sessionTypes = [
    'All',
    'Rolling',
    'Drilling',
    'Competition Prep',
    'Fundamentals',
    'Open Mat',
    'Private Lesson',
    'Boxing',
    'MMA'
  ];

  const filteredSessions = sessions.filter((session) => {
    const searchLower = searchTerm.toLowerCase().trim();
    const matchesType = selectedType === 'All' || session.sessionType === selectedType;

    if (!searchLower) return matchesType;

    const matchesPartner = session.partner?.toLowerCase().includes(searchLower);
    const matchesNotes = session.notes?.toLowerCase().includes(searchLower);
    const matchesSessionType = session.sessionType?.toLowerCase().includes(searchLower);
    const matchesDate = formatDate(session.date).toLowerCase().includes(searchLower);
    const matchesTechniques = session.techniques?.some((t) =>
      t.name.toLowerCase().includes(searchLower)
    );

    return matchesType && (matchesPartner || matchesNotes || matchesSessionType || matchesDate || matchesTechniques);
  });

  const getOutcomeBadge = (outcome) => {
    const colors = {
      Succeeded: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      Attempted: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      Taught: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    };
    return colors[outcome] || 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300';
  };

  const totalStats = {
    totalSessions: sessions.length,
    totalMatTime: sessions.reduce((sum, s) => sum + (s.duration || 0), 0),
    totalWins: sessions.reduce((sum, s) => sum + (s.wins || 0), 0),
    totalLosses: sessions.reduce((sum, s) => sum + (s.losses || 0), 0)
  };

  const winRate = totalStats.totalWins + totalStats.totalLosses > 0
    ? Math.round((totalStats.totalWins / (totalStats.totalWins + totalStats.totalLosses)) * 100)
    : 0;

  const handleDeleteSession = (sessionId, sessionType, sessionDate) => {
    const dateStr = formatDate(sessionDate);
    if (window.confirm(`Are you sure you want to delete this ${sessionType} session from ${dateStr}?\n\nThis action cannot be undone.`)) {
      deleteSession(sessionId);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Training Sessions</h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">History of your performance on the mats.</p>
        </div>
        <Link to="/session">
          <Button className="w-full sm:w-auto">
            <FiPlus className="w-4 h-4 mr-2" />
            Log New Session
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-600 dark:text-slate-400">Total Sessions</p><p className="text-xl font-bold dark:text-white">{totalStats.totalSessions}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-600 dark:text-slate-400">Mat Time</p><p className="text-xl font-bold dark:text-white">{formatDuration(totalStats.totalMatTime)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-600 dark:text-slate-400">Win Rate</p><p className="text-xl font-bold dark:text-white">{winRate}%</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-600 dark:text-slate-400">Record</p><p className="text-xl font-bold dark:text-white">{totalStats.totalWins}-{totalStats.totalLosses}</p></CardContent></Card>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              <Input
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="h-10 border rounded-md px-3 text-sm bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300"
            >
              {sessionTypes.map(type => <option key={type} value={type}>{type === 'All' ? 'All Types' : type}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredSessions.map((session) => (
          <Card 
            key={session.id} 
            className="hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 transition-all cursor-pointer"
            onClick={() => navigate(`/sessions/${session.id}/edit`)}
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold dark:text-white">{session.sessionType}</h3>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{session.sessionType}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                    <span className="flex items-center"><FiCalendar className="mr-1" /> {formatDate(session.date)}</span>
                    <span className="flex items-center"><FiClock className="mr-1" /> {formatDuration(session.duration)}</span>
                    {session.partner && <span className="flex items-center"><FiUser className="mr-1" /> {session.partner}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/sessions/${session.id}/edit`);
                    }}
                  >
                    <FiEdit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSession(session.id, session.sessionType, session.date);
                    }}
                    className="text-red-600 hover:bg-red-50 border-red-200 dark:text-red-400 dark:hover:bg-red-900/30 dark:border-red-800/50 transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {session.techniques?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {session.techniques.map((t, idx) => (
                    <span key={idx} className={`px-2 py-1 text-xs rounded-full ${getOutcomeBadge(t.outcome)}`}>
                      {t.name} • {t.outcome}
                    </span>
                  ))}
                </div>
              )}

              {session.notes && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{session.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredSessions.length === 0 && (
        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
          <FiActivity className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No sessions found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default Sessions;